import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  People as PeopleIcon,
  AutoStories as StoryIcon,
  Schedule as ScheduleIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface DashboardStats {
  total_campaigns: number;
  total_influencers: number;
  total_stories: number;
  active_campaigns: number;
  recent_stories_24h: number;
  active_influencers_7d: number;
  auto_check_enabled: number;
}

interface RecentActivity {
  recent_stories: any[];
  recent_posts: any[];
  recent_influencers: any[];
  recent_campaigns: any[];
  recent_updates: any[];
}

interface CampaignPerformance {
  id: number;
  title: string;
  start_date: string;
  end_date: string;
  total_influencers: number;
  shared_influencers: number;
  total_stories: number;
  participation_rate: number;
  auto_check_enabled: boolean;
  last_auto_check: string;
}

interface SystemLog {
  timestamp: string;
  message: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity | null>(null);
  const [campaignPerformance, setCampaignPerformance] = useState<CampaignPerformance[]>([]);
  const [systemLogs, setSystemLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const devLog = (...args: unknown[]) => {
      if (process.env.NODE_ENV === 'development') console.log(...args);
    };
    const devErr = (...args: unknown[]) => {
      if (process.env.NODE_ENV === 'development') console.error(...args);
    };
    try {
      devLog('[Dashboard] Fetching dashboard data...');
      
      // Test endpoint'ini dene
      try {
        const testRes = await axios.get('/api/campaigns?dashboard=stats');
        devLog('[Dashboard] Test endpoint OK:', testRes.data);
      } catch (testErr: any) {
        devErr('[Dashboard] Test endpoint failed:', testErr.response?.data || testErr.message);
      }
      
      // Tek tek çek ve hataları logla
      let statsRes, recentRes, performanceRes, logsRes;
      
      try {
        statsRes = await axios.get('/api/campaigns?dashboard=stats');
        devLog('[Dashboard] Stats loaded:', statsRes.data);
      } catch (err: any) {
        devErr('[Dashboard] Stats error:', err.response?.data || err.message);
        throw new Error('Stats failed: ' + (err.response?.data?.message || err.message));
      }
      
      try {
        recentRes = await axios.get('/api/campaigns?dashboard=recent-activities&limit=10');
        devLog('[Dashboard] Recent activities loaded');
      } catch (err: any) {
        devErr('[Dashboard] Recent activities error:', err.response?.data || err.message);
        recentRes = {
          data: {
            recent_stories: [],
            recent_posts: [],
            recent_influencers: [],
            recent_campaigns: [],
            recent_updates: []
          }
        };
      }

      try {
        performanceRes = await axios.get('/api/campaigns?dashboard=campaign-performance&limit=5');
        devLog('[Dashboard] Campaign performance loaded, count:', performanceRes.data.length);
      } catch (err: any) {
        devErr('[Dashboard] Campaign performance error:', err.response?.data || err.message);
        performanceRes = { data: [] };
      }

      try {
        logsRes = await axios.get('/api/campaigns?dashboard=system-logs');
        devLog('[Dashboard] System logs loaded, count:', logsRes.data.length);
      } catch (err: any) {
        devErr('[Dashboard] System logs error:', err.response?.data || err.message);
        logsRes = { data: [] };
      }

      setStats(statsRes.data);
      setRecentActivity(recentRes.data);
      setCampaignPerformance(performanceRes.data);
      setSystemLogs(logsRes.data);
      
      devLog('[Dashboard] All data loaded successfully');
    } catch (err: any) {
      console.error('[Dashboard] Fatal error:', err);
      setError(err.message || 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('tr-TR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const statCards = [
    {
      title: 'Toplam Kampanya',
      value: stats?.total_campaigns || 0,
      subtitle: `${stats?.active_campaigns || 0} aktif`,
      icon: <CampaignIcon sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      bgColor: '#e3f2fd'
    },
    {
      title: 'Toplam Influencer',
      value: stats?.total_influencers || 0,
      subtitle: 'kampanyalarda yer alan',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#dc004e',
      bgColor: '#fce4ec'
    },
    {
      title: 'Tespit Edilen Hikaye',
      value: stats?.total_stories || 0,
      subtitle: 'kampanyalarda tespit edilen',
      icon: <StoryIcon sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      bgColor: '#e8f5e9'
    },
    {
      title: 'Otomatik Takip',
      value: stats?.auto_check_enabled || 0,
      subtitle: 'kampanya aktif',
      icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
      color: '#ed6c02',
      bgColor: '#fff3e0'
    }
  ];

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">
        Dashboard
      </Typography>
        <Chip 
          icon={<UpdateIcon />} 
          label={`Son güncelleme: ${formatDateTime(new Date().toISOString())}`}
          size="small"
        />
      </Box>

      {/* İstatistik Kartları */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box 
                    sx={{ 
                      backgroundColor: card.bgColor, 
                      borderRadius: 2, 
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                  <Box sx={{ color: card.color }}>
                    {card.icon}
                    </Box>
                  </Box>
                </Box>
                <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  {card.value.toLocaleString('tr-TR')}
                </Typography>
                <Typography color="textSecondary" variant="body2" sx={{ mb: 0.5 }}>
                  {card.title}
                </Typography>
                <Typography variant="caption" color="primary">
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Kampanya Performansı */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Kampanya Performansı
              </Typography>
              {campaignPerformance.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Kampanya</TableCell>
                        <TableCell align="center">Influencer</TableCell>
                        <TableCell align="center">Katılım</TableCell>
                        <TableCell align="center">Story</TableCell>
                        <TableCell align="center">Durum</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaignPerformance.map((campaign) => (
                        <TableRow 
                          key={campaign.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (campaign.id) {
                              navigate(`/campaigns/${campaign.id}`);
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {campaign.title}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                      <Typography variant="body2">
                              {campaign.shared_influencers}/{campaign.total_influencers}
                      </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={Number(campaign.participation_rate || 0)}
                                sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                              />
                              <Typography variant="caption">
                                %{Number(campaign.participation_rate || 0).toFixed(0)}
                      </Typography>
                    </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={campaign.total_stories}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {campaign.auto_check_enabled ? (
                              <Chip 
                                icon={<ScheduleIcon />}
                                label="Otomatik"
                                size="small"
                                color="success"
                              />
                            ) : (
                              <Chip 
                                label="Manuel"
                                size="small"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">Henüz kampanya bulunmuyor</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Bu bölümler kaldırıldı */}

        {/* Son Eklenen Hikayeler */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Son Eklenen Hikayeler
              </Typography>
              {recentActivity && recentActivity.recent_stories.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Kullanıcı</TableCell>
                        <TableCell align="center">Sayı</TableCell>
                        <TableCell>Kampanya</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentActivity.recent_stories.map((story, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              @{story.kullanici}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={story.sayi}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {story.kampanya}
                </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">Henüz hikaye bulunmuyor</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Son Eklenen Gönderiler */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Son Eklenen Gönderiler
              </Typography>
              {recentActivity && (recentActivity.recent_posts || []).length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Kullanıcı</TableCell>
                        <TableCell align="center">Sayı</TableCell>
                        <TableCell>Kampanya</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(recentActivity.recent_posts || []).map((row: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              @{row.kullanici}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={row.sayi}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.kampanya}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">Henüz tespit edilen gönderi özeti bulunmuyor</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Alt Sütun - Sistem Logları */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Sistem Logları
              </Typography>
              {systemLogs.length > 0 ? (
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <List disablePadding>
                    {systemLogs.slice(0, 10).map((log, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <Divider />}
                        <ListItem disablePadding sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                                {log.message}
                      </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="textSecondary" fontSize="0.7rem">
                                {new Date(log.timestamp).toLocaleString('tr-TR')}
                      </Typography>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                  ))}
                  </List>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <UpdateIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="textSecondary">
                    Henüz log bulunmuyor
                </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
